import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, ShieldCheck, Smartphone, KeyRound, Fingerprint, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
    IdentityApiError,
    useSession,
    useMfaStatus,
    useMfaPolicy,
    useEnrollMfa,
    useEnableMfa,
    useDisableMfa,
    usePasskeys,
    useRegisterPasskey,
    useDeletePasskey,
    useAuthenticators,
    useSetAuthenticatorPasswordless,
    useRemoveAuthenticator
} from "@/api/hooks";
import type { AuthenticatorDto, MfaEnrollResponse } from "@/api/identityApi";
import { passkeysSupported } from "@/lib/webauthn";
import { cn } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";

export function SecurityPage() {
    const session = useSession();
    const authed = session.data?.isAuthenticated === true;
    const policy = useMfaPolicy(authed);
    // Fail-open: show a method unless the domain policy explicitly forbids it.
    const allowAuthenticator = policy.data?.allowAuthenticator !== false;
    const allowBiometric = policy.data?.allowBiometric !== false;
    const status = useMfaStatus(authed);
    const enroll = useEnrollMfa();
    const enable = useEnableMfa();
    const disable = useDisableMfa();
    const navigate = useNavigate();

    const [enrollData, setEnrollData] = useState<MfaEnrollResponse | null>(null);
    const [code, setCode] = useState("");
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    if (session.data && !session.data.isAuthenticated) {
        navigate("/login?returnUrl=/security", { replace: true });
    }

    const startEnroll = async () => {
        setError(null);
        setRecoveryCodes(null);
        try {
            setEnrollData(await enroll.mutateAsync());
        } catch (err) {
            setError(err instanceof IdentityApiError ? err.message : "Couldn't start enrollment.");
        }
    };

    const confirmEnable = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        try {
            const resp = await enable.mutateAsync(code.trim());
            setRecoveryCodes(resp.recoveryCodes);
            setEnrollData(null);
            setCode("");
        } catch (err) {
            setError(
                err instanceof IdentityApiError
                    ? (err.detail ?? err.message)
                    : "Couldn't verify that code."
            );
        }
    };

    const turnOff = async () => {
        setError(null);
        if (!window.confirm("Turn off two-factor authentication? Your account will be less secure.")) return;
        try {
            await disable.mutateAsync();
            setEnrollData(null);
            setRecoveryCodes(null);
        } catch (err) {
            setError(err instanceof IdentityApiError ? err.message : "Couldn't disable 2FA.");
        }
    };

    const enabled = status.data?.enabled === true;
    // 2FA can be on through phone approvals alone (Athavita Authenticator), with no code app.
    const hasCodeApp = status.data?.hasAuthenticator === true;

    return (
        <div className="w-full space-y-6">
        {allowBiometric ? <PasskeyCard authed={authed} /> : null}
        {allowAuthenticator ? <AuthenticatorCard authed={authed} /> : null}
        {allowAuthenticator ? (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary" /> Two-factor authentication
                </CardTitle>
                <CardDescription>
                    Add a second step at sign-in using an authenticator app
                    (Google Authenticator, Microsoft Authenticator, 1Password, Authy…).
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {error ? (
                    <Alert variant="destructive">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Something went wrong</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}

                {/* One-time recovery codes, shown right after enabling */}
                {recoveryCodes ? (
                    <Alert>
                        <KeyRound className="h-4 w-4" />
                        <AlertTitle>Save your recovery codes</AlertTitle>
                        <AlertDescription>
                            <p className="mb-2">
                                Store these somewhere safe. Each code works once if you lose your
                                authenticator. They won't be shown again.
                            </p>
                            <div className="grid grid-cols-2 gap-1 rounded-md border bg-muted/40 p-3 font-mono text-sm">
                                {recoveryCodes.map((c) => (
                                    <span key={c}>{c}</span>
                                ))}
                            </div>
                        </AlertDescription>
                    </Alert>
                ) : null}

                {status.isPending ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : enabled && !enrollData ? (
                    <div className="space-y-3">
                        <Alert>
                            <ShieldCheck className="h-4 w-4" />
                            <AlertTitle>Two-factor is on</AlertTitle>
                            <AlertDescription>
                                {hasCodeApp
                                    ? "You'll be asked for a code from your authenticator app each time you sign in."
                                    : "Sign-ins are approved on your phone with Athavita Authenticator. You can also add a code app as a backup."}
                            </AlertDescription>
                        </Alert>
                        <div className="flex flex-wrap gap-2">
                            {!hasCodeApp ? (
                                <Button onClick={startEnroll} disabled={enroll.isPending}>
                                    <Smartphone className="h-4 w-4" />
                                    {enroll.isPending ? "Preparing…" : "Add a code app"}
                                </Button>
                            ) : null}
                            <Button variant="outline" onClick={turnOff} disabled={disable.isPending}>
                                {disable.isPending ? "Turning off…" : "Turn off two-factor"}
                            </Button>
                        </div>
                    </div>
                ) : enrollData ? (
                    <div className="space-y-4">
                        <div className="rounded-md border p-4">
                            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                                <Smartphone className="h-4 w-4" /> Scan with your authenticator app
                            </div>
                            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                                <div className="rounded-lg border bg-white p-3">
                                    <QRCodeSVG value={enrollData.authenticatorUri} size={160} level="M" />
                                </div>
                                <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                                    <li>Open Google / Microsoft Authenticator (or 1Password, Authy…).</li>
                                    <li>Tap <strong>＋</strong> → <strong>Scan a QR code</strong> and point it at this code.</li>
                                    <li>Enter the 6-digit code it shows below to finish.</li>
                                </ol>
                            </div>
                            <details className="mt-3">
                                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                                    Can’t scan? Enter a setup key instead
                                </summary>
                                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                    <p>Account name: your email · type: time-based. Setup key:</p>
                                    <div className="select-all break-all rounded bg-muted/50 px-2 py-1 font-mono text-sm text-foreground">
                                        {enrollData.sharedKey}
                                    </div>
                                </div>
                            </details>
                        </div>
                        <form onSubmit={confirmEnable} className="space-y-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="verify-code">Authenticator code</Label>
                                <Input
                                    id="verify-code"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="123456"
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit" disabled={enable.isPending || !code.trim()}>
                                    {enable.isPending ? "Verifying…" : "Verify & turn on"}
                                </Button>
                                <Button type="button" variant="ghost" onClick={() => { setEnrollData(null); setCode(""); }}>
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Two-factor authentication is off. Turning it on protects your account even
                            if your password is stolen.
                        </p>
                        <Button onClick={startEnroll} disabled={enroll.isPending}>
                            <Smartphone className="h-4 w-4" />
                            {enroll.isPending ? "Preparing…" : "Set up authenticator app"}
                        </Button>
                    </div>
                )}
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
                Signed in as {session.data?.email ?? "—"}
            </CardFooter>
        </Card>
        ) : null}
        </div>
    );
}

function authenticatorLabel(a: AuthenticatorDto): string {
    return a.name?.trim() || a.model?.trim() || (a.platform === "ios" ? "iPhone" : "Android phone");
}

function formatDate(value: string | null): string | null {
    return value ? new Date(value).toLocaleDateString() : null;
}

/** Phones running Athavita Authenticator that approve sign-ins (Mobile Phase 1). */
function AuthenticatorCard({ authed }: { authed: boolean }) {
    const list = useAuthenticators(authed);
    const setPasswordless = useSetAuthenticatorPasswordless();
    const remove = useRemoveAuthenticator();
    const [error, setError] = useState<string | null>(null);
    const rows = list.data ?? [];

    const toggle = async (a: AuthenticatorDto) => {
        setError(null);
        try {
            await setPasswordless.mutateAsync({ deviceId: a.deviceId, passwordless: !a.passwordless });
        } catch (err) {
            setError(err instanceof IdentityApiError ? (err.detail ?? err.message) : "Couldn't change that setting.");
        }
    };

    const removeOne = async (a: AuthenticatorDto) => {
        setError(null);
        const confirmed = window.confirm(
            `Remove ${authenticatorLabel(a)}? It will stop approving your sign-ins. ` +
            "If it's your only way to verify, two-factor authentication will be turned off."
        );
        if (!confirmed) return;
        try {
            await remove.mutateAsync(a.deviceId);
        } catch (err) {
            setError(err instanceof IdentityApiError ? (err.detail ?? err.message) : "Couldn't remove that phone.");
        }
    };

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-primary" /> Athavita Authenticator
                </CardTitle>
                <CardDescription>
                    Approve sign-ins on your phone: tap the number shown on screen, then confirm with your
                    fingerprint, face or passcode.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {error ? (
                    <Alert variant="destructive">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Something went wrong</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}

                {list.isPending ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : rows.length > 0 ? (
                    <ul className="divide-y rounded-md border">
                        {rows.map((a) => {
                            const added = formatDate(a.enrolledAt);
                            const used = formatDate(a.lastUsedAt);
                            return (
                                <li
                                    key={a.deviceId}
                                    className="flex flex-col gap-3 px-3 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="flex min-w-0 items-start gap-2">
                                        <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                        <div className="min-w-0">
                                            <div className="truncate font-medium">{authenticatorLabel(a)}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {a.platform === "ios" ? "iOS" : "Android"}
                                                {added ? ` · added ${added}` : ""}
                                                {used ? ` · last used ${used}` : " · not used yet"}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={a.passwordless}
                                                aria-label={`Passwordless sign-in with ${authenticatorLabel(a)}`}
                                                disabled={setPasswordless.isPending}
                                                onClick={() => void toggle(a)}
                                                className={cn(
                                                    "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50",
                                                    a.passwordless ? "bg-primary" : "bg-input"
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        "pointer-events-none block h-4 w-4 rounded-full bg-background shadow transition-transform",
                                                        a.passwordless ? "translate-x-4" : "translate-x-0"
                                                    )}
                                                />
                                            </button>
                                            <span className="text-xs text-muted-foreground">Passwordless</span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Remove ${authenticatorLabel(a)}`}
                                            disabled={remove.isPending}
                                            onClick={() => void removeOne(a)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        No phones yet. Install Athavita Authenticator, sign in with your work account and turn on
                        sign-in approvals. If you don't have two-step verification yet, the app shows your recovery
                        codes once — save them.
                    </p>
                )}

                {rows.length > 0 ? (
                    <p className="text-xs text-muted-foreground">
                        Passwordless lets you sign in with just your email and a tap on that phone.
                    </p>
                ) : null}
            </CardContent>
        </Card>
    );
}

function PasskeyCard({ authed }: { authed: boolean }) {
    const list = usePasskeys(authed);
    const register = useRegisterPasskey();
    const remove = useDeletePasskey();
    const [error, setError] = useState<string | null>(null);
    const supported = passkeysSupported();

    const add = async () => {
        setError(null);
        try {
            await register.mutateAsync();
        } catch (err) {
            // A user cancelling the browser prompt throws NotAllowedError — treat as a quiet no-op.
            if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError")) return;
            setError(
                err instanceof IdentityApiError ? err.message
                    : err instanceof Error ? err.message
                    : "Couldn't add a passkey."
            );
        }
    };

    const rows = list.data ?? [];

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Fingerprint className="h-5 w-5 text-primary" /> Passkeys
                </CardTitle>
                <CardDescription>
                    Sign in with your fingerprint, face, or device PIN — no code to type.
                    A passkey is tied to this device (or your phone/security key).
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {!supported ? (
                    <Alert variant="destructive">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Not supported here</AlertTitle>
                        <AlertDescription>This browser doesn't support passkeys.</AlertDescription>
                    </Alert>
                ) : null}
                {error ? (
                    <Alert variant="destructive">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Couldn't add a passkey</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}

                {rows.length > 0 ? (
                    <ul className="divide-y rounded-md border">
                        {rows.map((p) => (
                            <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                                <span className="flex items-center gap-2">
                                    <KeyRound className="h-4 w-4 text-muted-foreground" />
                                    <span>
                                        {p.deviceName}
                                        <span className="ml-2 text-xs text-muted-foreground">
                                            added {new Date(p.createdAt).toLocaleDateString()}
                                            {p.lastUsedAt ? ` · last used ${new Date(p.lastUsedAt).toLocaleDateString()}` : ""}
                                        </span>
                                    </span>
                                </span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Remove passkey"
                                    onClick={() => { if (window.confirm("Remove this passkey?")) remove.mutate(p.id); }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-muted-foreground">No passkeys yet.</p>
                )}

                <Button onClick={add} disabled={!supported || register.isPending}>
                    <Fingerprint className="h-4 w-4" />
                    {register.isPending ? "Follow your device prompt…" : "Add a passkey"}
                </Button>
            </CardContent>
        </Card>
    );
}
